<template>
  <div class="transactions-page">
    <div class="page-header">
      <h2>💳 تراکنش‌های بیعانه</h2>
      <p class="subtitle">مشاهده و جستجوی تراکنش‌های بیعانه سالن</p>
    </div>

    <!-- ========================================== -->
    <!-- خلاصه آمار -->
    <!-- ========================================== -->
    <div v-if="summary" class="stats-grid">
      <div class="stat-card">
        <div class="stat-icon">💰</div>
        <div class="stat-content">
          <span class="stat-value">{{ formatPrice(summary.total_paid) }}</span>
          <span class="stat-label">مجموع پرداخت شده</span>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon">⏳</div>
        <div class="stat-content">
          <span class="stat-value">{{ formatPrice(summary.total_pending) }}</span>
          <span class="stat-label">در انتظار پرداخت</span>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon">🔄</div>
        <div class="stat-content">
          <span class="stat-value">{{ formatPrice(summary.total_refunded) }}</span>
          <span class="stat-label">عودت داده شده</span>
        </div>
      </div>

    </div>

    <!-- ========================================== -->
    <!-- فیلترها -->
    <!-- ========================================== -->
    <div class="filter-section">
      <div class="filter-row">
        <!-- نوع فیلتر -->
        <div class="filter-group">
          <label>فیلتر بر اساس</label>
          <select v-model="filters.filter_type" class="filter-select">
            <option value="reservation">تاریخ رزرو</option>
            <option value="payment">تاریخ پرداخت</option>
          </select>
        </div>

        <!-- از تاریخ -->
        <div class="filter-group">
          <label>از تاریخ</label>
          <data-picker v-model="filters.date_from" type="date" class="" />
        </div>

        <!-- تا تاریخ -->
        <div class="filter-group">
          <label>تا تاریخ</label>
          <data-picker v-model="filters.date_to" type="date" class="" />
        </div>

        <!-- وضعیت -->
        <div class="filter-group">
          <label>وضعیت</label>
          <select v-model="filters.status" class="filter-select">
            <option value="">همه</option>
            <option value="pending">در انتظار پرداخت</option>
            <option value="paid">پرداخت شده</option>
            <option value="failed">ناموفق</option>
            <option value="refunded">عودت داده شده</option>
          </select>
        </div>

        <button class="btn-filter" @click="loadTransactions">🔍 اعمال فیلتر</button>
        <button class="btn-reset" @click="resetFilters">⟳ پاک کردن</button>
      </div>
    </div>

    <!-- ========================================== -->
    <!-- لیست تراکنش‌ها -->
    <!-- ========================================== -->
    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <span>در حال بارگذاری...</span>
    </div>

    <div v-else-if="transactions.length === 0" class="empty-state">
      <div class="empty-icon">💳</div>
      <h3>هیچ تراکنشی یافت نشد</h3>
      <p>با توجه به فیلترهای انتخاب شده، تراکنشی یافت نشد.</p>
    </div>

    <div v-else>
      <!-- ===== دسکتاپ: جدول ===== -->
      <div class="desktop-table">
        <table>
          <thead>
          <tr>
            <th>#</th>
            <th>شماره رزرو</th>
            <th>مشتری</th>
            <th>خدمت</th>
            <th>مبلغ (تومان)</th>
            <th>تاریخ رزرو</th>
            <th>تاریخ پرداخت</th>
            <th>وضعیت</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="(item, index) in transactions" :key="item.id">
            <td>{{ index + 1 }}</td>
            <td>{{ item.order_id }}</td>
            <td>
              <span class="user-name">{{ item.user ? item.user.name : 'نامشخص' }}</span>
              <br>
              <small class="user-phone">{{ item.user ? item.user.phone : '' }}  - {{item.admin.name}}</small>
            </td>
            <td>{{ item.line ? item.line.title : '-' }}</td>
            <td class="amount">{{ formatPrice(item.amount) }}</td>
            <td>{{ get_date_time(item.reservation_date) }}</td>
            <td>{{ item.paid_at ? get_date_time(item.paid_at) : '-' }}</td>
            <td>
                <span :class="['badge', statusClass(item.status)]">
                  {{ item.status_label }}
                </span>
                <span :class="['badge', orderStatusClass(item.order.status)]">
                  {{ orderStatusLabel(item.order.status) }}
                </span>

            </td>
          </tr>
          </tbody>
        </table>
      </div>

      <!-- ===== موبایل: کارت ===== -->
      <div class="mobile-cards">
        <div v-for="(item, index) in transactions" :key="item.id" class="card-item">
          <div class="card-header">
            <span class="card-index">#{{ item.order_id }}</span>
            <span :class="['badge', statusClass(item.status)]">
              {{ item.status_label }}
            </span>
            <span :class="['badge', orderStatusClass(item.order.status)]">
                  {{ orderStatusLabel(item.order.status) }}
                </span>

          </div>
          <div class="card-body">
            <div class="card-row">
              <span class="card-label">مشتری:</span>
              <span>{{ item.user ? item.user.name : 'نامشخص' }} </span>
            </div>
            <div class="card-row">
              <span class="card-label">خدمت:</span>
              <span>{{ item.line ? item.line.title : '-' }}  - {{item.admin.name}}</span>
            </div>
            <div class="card-row">
              <span class="card-label">مبلغ:</span>
              <span class="amount">{{ formatPrice(item.amount) }}</span>
            </div>
            <div class="card-row">
              <span class="card-label">تاریخ رزرو:</span>
              <span>{{ get_date_time(item.reservation_date) }}</span>
            </div>
            <div class="card-row">
              <span class="card-label">تاریخ پرداخت:</span>
              <span>{{ item.paid_at ? get_date_time(item.paid_at) : '-' }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- ===== صفحه‌بندی ===== -->
      <div v-if="pagination.total > 0" class="pagination">
        <button
            class="btn-page"
            :disabled="pagination.current_page <= 1"
            @click="handlePageChange(pagination.current_page - 1)"
        >
          قبلی
        </button>
        <span class="page-info">
          صفحه {{ pagination.current_page }} از {{ pagination.last_page }}
        </span>
        <button
            class="btn-page"
            :disabled="pagination.current_page >= pagination.last_page"
            @click="handlePageChange(pagination.current_page + 1)"
        >
          بعدی
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'DepositTransactions',

  data() {
    return {
      loading: false,
      transactions: [],
      summary: null,
      filters: {
        filter_type: 'reservation',
        date_from: '',
        date_to: '',
        status: ''
      },
      pagination: {
        total: 0,
        per_page: 20,
        current_page: 1,
        last_page: 1
      }
    };
  },

  mounted() {
    this.loadTransactions();
  },

  methods: {
    // ==========================================
    // بارگذاری تراکنش‌ها
    // ==========================================
    async loadTransactions() {
      this.loading = true;
      try {
        const params = {
          filter_type: this.filters.filter_type,
          date_from: this.filters.date_from || undefined,
          date_to: this.filters.date_to || undefined,
          status: this.filters.status || undefined,
          page: this.pagination.current_page,
          per_page: this.pagination.per_page
        };

        const { data } = await axios.get('/api/admin/deposit-transactions', { params:params});

        this.transactions = data.data || [];
        this.summary = data.summary || null;
        this.pagination = data.pagination || {
          total: 0,
          per_page: 20,
          current_page: 1,
          last_page: 1
        };

      } catch (error) {
        console.error('خطا:', error);
        this.$message?.error('خطا در بارگذاری اطلاعات');
      } finally {
        this.loading = false;
      }
    },

    // ==========================================
    // تغییر صفحه
    // ==========================================
    handlePageChange(page) {
      if (page < 1 || page > this.pagination.last_page) return;
      this.pagination.current_page = page;
      this.loadTransactions();
    },

    // ==========================================
    // ریست فیلترها
    // ==========================================
    resetFilters() {
      this.filters = {
        filter_type: 'reservation',
        date_from: '',
        date_to: '',
        status: ''
      };
      this.pagination.current_page = 1;
      this.loadTransactions();
    },

    // ==========================================
    // توابع کمکی
    // ==========================================
    formatPrice(value) {
      if (!value) return '۰';
      return new Intl.NumberFormat('fa-IR').format(parseFloat(value)) + ' تومان';
    },

    formatDate(date) {
      if (!date) return '-';
      const d = new Date(date);
      return d.getFullYear() + '/' +
          String(d.getMonth() + 1).padStart(2, '0') + '/' +
          String(d.getDate()).padStart(2, '0');
    },

    formatDateTime(date) {
      if (!date) return '-';
      const d = new Date(date);
      return this.formatDate(date) + ' ' +
          String(d.getHours()).padStart(2, '0') + ':' +
          String(d.getMinutes()).padStart(2, '0');
    },

    statusClass(status) {
      const map = {
        pending: 'badge-warning',
        paid: 'badge-success',
        failed: 'badge-danger',
        refunded: 'badge-gray'
      };
      return map[status] || 'badge-gray';
    },
    orderStatusClass(status) {
      const map = {
        1: 'badge-success',
        0: 'badge-danger',
        2: 'badge-primary',
      };
      return map[status] || 'badge-gray';
    },
    orderStatusLabel(status) {
      const map = {
        1: 'رزرو فعال',
        0: 'رزرو کنسل شده',
        2: 'مراجعه شده',
      };
      return map[status] || 'badge-gray';
    }
  }
};
</script>

<style scoped>
.transactions-page {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
  direction: rtl;
}

.page-header {
  margin-bottom: 24px;
}

.page-header h2 {
  margin: 0;
  color: #2c3e50;
  font-size: 24px;
}

.page-header .subtitle {
  margin: 4px 0 0 0;
  color: #909399;
  font-size: 14px;
}

/* ===== آمار ===== */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
}

.stat-card {
  background: white;
  border-radius: 12px;
  padding: 16px 18px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  display: flex;
  align-items: center;
  gap: 14px;
}

.stat-icon {
  font-size: 28px;
}

.stat-content {
  display: flex;
  flex-direction: column;
}

.stat-value {
  font-size: 18px;
  font-weight: bold;
  color: #2c3e50;
}

.stat-label {
  font-size: 12px;
  color: #909399;
}

/* ===== فیلترها ===== */
.filter-section {
  background: white;
  padding: 16px 20px;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  margin-bottom: 20px;
}

.filter-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
}

.filter-group {
  display: flex;
  align-items: center;
  gap: 6px;
}

.filter-group label {
  font-size: 13px;
  color: #606266;
  font-weight: 500;
}

.filter-select,
.filter-input {
  padding: 6px 12px;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  font-size: 13px;
  background: white;
}

.filter-input {
  min-width: 140px;
}

.btn-filter {
  padding: 6px 20px;
  background: #409eff;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
}

.btn-filter:hover {
  background: #66b1ff;
}

.btn-reset {
  padding: 6px 16px;
  background: #f5f7fa;
  color: #606266;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
}

.btn-reset:hover {
  background: #e4e7ed;
}

/* ===== جدول ===== */
.desktop-table {
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  overflow-x: auto;
}

.desktop-table table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  min-width: 800px;
}

.desktop-table thead th {
  background: #f5f7fa;
  color: #606266;
  font-weight: 600;
  padding: 12px 14px;
  text-align: right;
  border-bottom: 2px solid #e4e7ed;
}

.desktop-table tbody td {
  padding: 10px 14px;
  border-bottom: 1px solid #ebeef5;
  color: #303133;
}

.desktop-table tbody tr:hover td {
  background: #f5f7fa;
}

.user-name {
  font-weight: 500;
}

.user-phone {
  color: #909399;
  font-size: 11px;
}

.amount {
  font-weight: 600;
  color: #2c3e50;
}

/* ===== بج‌ها ===== */
.badge {
  display: inline-block;
  padding: 2px 12px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
}

.badge-success {
  background: #f0f9eb;
  color: #67c23a;
}

.badge-warning {
  background: #fdf6ec;
  color: #e6a23c;
}

.badge-danger {
  background: #fef0f0;
  color: #f56c6c;
}

.badge-gray {
  background: #f4f4f5;
  color: #909399;
}

/* ===== حالت‌ها ===== */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 60px 20px;
  color: #909399;
  gap: 12px;
  background: white;
  border-radius: 10px;
}

.spinner {
  width: 36px;
  height: 36px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #409eff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
  background: white;
  border-radius: 10px;
}

.empty-icon {
  font-size: 48px;
  margin-bottom: 12px;
}

/* ===== صفحه‌بندی ===== */
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  margin-top: 20px;
}

.btn-page {
  padding: 6px 16px;
  background: #409eff;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
}

.btn-page:hover:not(:disabled) {
  background: #66b1ff;
}

.btn-page:disabled {
  background: #c0c4cc;
  cursor: not-allowed;
}

.page-info {
  font-size: 13px;
  color: #606266;
}

/* ===== کارت‌های موبایل ===== */
.mobile-cards {
  display: none;
}

.card-item {
  background: white;
  border-radius: 8px;
  padding: 14px 16px;
  margin-bottom: 10px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f0f0f0;
}

.card-index {
  font-weight: 600;
  color: #909399;
  font-size: 13px;
}

.card-body {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.card-row {
  display: flex;
  gap: 6px;
  font-size: 13px;
  color: #303133;
  flex-wrap: wrap;
}

.card-label {
  font-weight: 500;
  color: #909399;
  min-width: 80px;
}

/* ===== ریسپانسیو ===== */
@media (max-width: 768px) {
  .transactions-page {
    padding: 12px;
  }

  .filter-row {
    flex-direction: column;
    align-items: stretch;
  }

  .filter-group {
    flex-direction: column;
    align-items: stretch;
  }

  .filter-input,
  .filter-select {
    width: 100%;
  }

  .btn-filter,
  .btn-reset {
    width: 100%;
    text-align: center;
  }

  .desktop-table {
    display: none;
  }

  .mobile-cards {
    display: block;
  }

  .stats-grid {
    grid-template-columns: 1fr;
  }
}
</style>